import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Plus, Trash2 } from "lucide-react";
import * as api from "../../lib/crmApi";
import CrmLeadDrawer from "./CrmLeadDrawer";
import CrmCreateLeadModal from "./CrmCreateLeadModal";
import ExportMenu from "./ExportMenu";

const fmtDkk = (v) => new Intl.NumberFormat("da-DK").format(v || 0) + " kr.";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("da-DK") : "—";

export default function CrmLeads() {
    const [leads, setLeads] = useState([]);
    const [stages, setStages] = useState([]);
    const [q, setQ] = useState("");
    const [stageFilter, setStageFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [openLead, setOpenLead] = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (stageFilter) params.stage = stageFilter;
            const data = await api.listLeads(params);
            setLeads(data);
        } catch { toast.error("Kunne ikke hente leads"); }
        finally { setLoading(false); }
    };

    useEffect(() => { refresh(); api.getStages().then(setStages); }, []); // eslint-disable-line
    useEffect(() => { const t = setTimeout(refresh, 300); return () => clearTimeout(t); }, [q, stageFilter]); // eslint-disable-line

    const remove = async (id) => {
        if (!window.confirm("Slet dette lead? Handlingen kan ikke fortrydes.")) return;
        try { await api.deleteLead(id); refresh(); toast.success("Slettet"); } catch { toast.error("Fejl"); }
    };

    return (
        <div className="p-6 lg:p-8" data-testid="crm-leads-list">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-3xl font-bold text-slate-900">Alle leads</h1>
                <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10">
                    <Plus className="h-4 w-4" /> Opret lead
                </button>
            </div>

            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg..."
                        data-testid="crm-leads-search"
                        className="pl-9 w-full h-10 rounded-lg border border-slate-300 bg-white text-sm" />
                </div>
                <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                    data-testid="crm-leads-stage-filter"
                    className="h-10 rounded-lg border border-slate-300 bg-white text-sm px-3">
                    <option value="">Alle trin</option>
                    {stages.map((s) => <option key={s}>{s}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-slate-500 hidden sm:inline">{leads.length} lead{leads.length === 1 ? "" : "s"}</span>
                    <ExportMenu body={{ q: q || undefined, stage: stageFilter || undefined }} label={q || stageFilter ? "Eksportér filtrerede" : "Eksportér alle"} />
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                                {["Virksomhed", "Kontakt", "E-mail", "Telefon", "Værdi", "Trin", "Oprettet", ""].map((h) => (
                                    <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((l) => (
                                <tr key={l.id} onClick={() => setOpenLead(l.id)}
                                    data-testid={`crm-lead-row-${l.id}`}
                                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                                    <td className="px-4 py-3 font-semibold text-slate-900">{l.company}</td>
                                    <td className="px-4 py-3 text-slate-700">{l.contact_name}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.email}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.phone}</td>
                                    <td className="px-4 py-3 font-semibold text-sky-700">{fmtDkk(l.value_dkk)}</td>
                                    <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 font-medium">{l.stage}</span></td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(l.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <button onClick={(e) => { e.stopPropagation(); remove(l.id); }} className="text-rose-500 hover:text-rose-700" title="Slet">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {leads.length === 0 && (
                                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">Ingen leads matcher filteret</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreate && <CrmCreateLeadModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
            {openLead && <CrmLeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} onChanged={refresh} />}
        </div>
    );
}
